/**
 * Plan API service.
 *
 * Reads the current plan/entitlement and redeems activation codes to upgrade.
 * Enforcement limits come from the backend so the frontend does not hardcode
 * them.
 *
 * @module planApi
 */

import { apiGet, apiPost } from './apiUtils';

/** Resolved limits for a plan tier (authoritative copy from the backend). */
export interface PlanLimits {
  plan_id: string;
  display_name: string;
  price: string;
  mock_quota: number | null;
  allowed_modules: string[];
  ai_feedback: boolean;
  level_scope: string;
  provider_scope: string;
  duration_days: number | null;
}

/** The user's current effective entitlement. */
export interface CurrentPlan {
  plan_id: string;
  chosen_level: string | null;
  chosen_provider: string | null;
  /** Per-module attempt quota (null = unlimited). */
  mock_quota: number | null;
  /** Attempts used per module in the current window, e.g. { reading: 3, writing: 1 }. */
  module_usage: Record<string, number>;
  activated_at: string | null;
  expires_at: string | null;
  is_expired: boolean;
  /** True when a lapsed paid pass must be renewed before paid actions. */
  requires_renewal: boolean;
  limits: PlanLimits;
  /** Most recently expired pass, for a dismissible dashboard notice (null when none). */
  recent_expiry?: RecentExpiry | null;
}

/** A recently expired pass surfaced as a dismissible dashboard notice. */
export interface RecentExpiry {
  plan_id: string;
  display_name: string;
  /** ISO timestamp of when the pass expired/was archived. */
  expired_at: string;
}

export interface RedeemPlanRequest {
  tier: string;
  level?: string | null;
  code: string;
}

export interface RedeemPlanResponse {
  success: boolean;
  message: string;
  plan: CurrentPlan;
}

/**
 * Fetch the authenticated user's current plan/entitlement.
 *
 * @returns The current plan.
 * @throws {Error} If the request fails.
 */
export const getCurrentPlan = async (): Promise<CurrentPlan> => {
  const result = await apiGet<CurrentPlan>('/plans/me', {
    errorMessage: 'Failed to load your plan',
    context: 'getCurrentPlan',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to load your plan');
  }
  return result;
};

/**
 * Redeem an activation code to activate the selected tier.
 *
 * @param request - Selected tier, chosen level (for level-locked tiers), and code.
 * @returns The redemption response including the updated plan.
 * @throws {Error} With the backend message if the code/tier/level is invalid.
 */
export const redeemPlan = async (
  request: RedeemPlanRequest,
): Promise<RedeemPlanResponse> => {
  const result = await apiPost<RedeemPlanResponse>('/plans/redeem', request, {
    errorMessage: 'Failed to activate plan',
    context: 'redeemPlan',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to activate plan');
  }
  return result;
};
